import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #apronHeadingObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#syncMasonicApronHeadings();
    this.#apronHeadingObserver = new MutationObserver(() => this.#syncMasonicApronHeadings());
    this.#apronHeadingObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#apronHeadingObserver?.disconnect();
  }

  #syncMasonicApronHeadings() {
    const url = new URL(window.location.href);
    const path = url.pathname.replace(/\/$/, '');
    const isAprons = path === '/collections/aprons';
    const isSale = path === '/collections/sale';
    const isAccessories = path === '/collections/accessories';
    const isAllTies = path === '/collections/all-ties';
    if (!isAprons && !isSale && !isAccessories && !isAllTies) return;

    const hasActiveFilters = [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
    const sortBy = url.searchParams.get('sort_by');
    if (hasActiveFilters || (sortBy && sortBy !== 'manual')) return;

    const grid = this.querySelector('[data-testid="product-grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const sections = isSale
      ? [
          ['4363397431361', 'Caps & Headwear'],
          ['4363393073217', 'Ties & Bow Ties'],
          ['4363394252865', 'Pocket Squares'],
          ['4363385962561', 'Aprons & Regalia'],
          ['4363396546625', 'Jewelry & Pins'],
          ['4363385995329', 'Accessories & Gifts'],
        ]
      : isAccessories
        ? [
            ['4363385995329', 'Pens & Writing Instruments'],
            ['4363384160321', 'Tie Bars & Tie Tacks'],
            ['4363383308353', 'Jewelry & Formal Accessories'],
            ['4363385438273', 'Embroidery Patches & Regalia'],
            ['4472885116993', 'Everyday Accessories & Gifts'],
          ]
        : isAllTies
          ? [
              ['4363393073217', 'Neckties'],
              ['4363391631425', 'Pre-Tied Bow Ties'],
              ['4363391139905', 'Self-Tie Bow Ties'],
            ]
          : [
              ['4363385962561', 'Apron Accessories'],
              ['4363388911681', 'Blue Lodge — Members'],
              ['4363386945601', 'Blue Lodge — Officers'],
              ['4363388026945', 'Past Masters'],
              ['4363390124097', 'Grand Lodge'],
              ['4363384127553', 'Scottish Rite'],
              ['6751652577345', 'Combined York Rite'],
              ['4363387928641', 'York Rite Chapter'],
              ['4363388092481', 'York Rite Cryptic Council'],
              ['4363386814529', 'York Rite Commandery'],
              ['4363386650689', 'Other Masonic Bodies'],
            ];

    for (const [productId, title] of sections) {
      const item = grid.querySelector(`.product-grid__item[data-product-id="${productId}"]`);
      if (!(item instanceof HTMLElement)) continue;

      const previous = item.previousElementSibling;
      if (
        previous?.classList.contains('product-grid__masonic-section-heading') &&
        previous.textContent?.trim() === title
      ) {
        continue;
      }

      if (grid.querySelector(`[data-masonic-section-for="${productId}"]`)) continue;

      const heading = document.createElement('li');
      heading.className = 'product-grid__masonic-section-heading';
      heading.setAttribute('role', 'presentation');
      heading.setAttribute('data-masonic-section-for', productId);

      const headingTitle = document.createElement('h2');
      headingTitle.className = 'product-grid__masonic-section-title';
      headingTitle.textContent = title;
      heading.appendChild(headingTitle);

      grid.insertBefore(heading, item);
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change event
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
